import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';

import { trendingProperties } from '@vi-iot/client';

import {
  activeErrorsEndpoints,
  backupEndpoints,
  dataHemsEndpoints,
  dataTrendingEndpoints,
  errorDatabaseErrors,
  gtsEndpoints,
  hydraulicSchemesEndpoints,
  initEndpoints,
  installationEndpoints,
  mockProFunctionalitiesEndpoints,
  snapshotEndpoints,
  useCasesEndpoints,
} from '@common';
import { RemoteDiagnosticsSnapshotsService } from '@store';
import moment from 'moment';

import { flatten } from 'lodash-es';
import {
  MockedEndpoint,
  RemoteServiceMockEndpointDetails,
  TrendingHelperMockEndpointDetails,
} from '../common/demo.model';
import { environment } from '../../../environments/environment';

import * as Vdds from '../../../projects/client/apis/remote-diagnostics/mocked-vdds/index';
import { MockedVdd } from '../../../projects/client/apis/remote-diagnostics/mocked-vdds/MockedVdd.model';
import { modifyRequestUrlForDemoMode } from './interceptor-service-helper';

@Injectable()
export class DemoModeService {
  private mockedData: MockedEndpoint[];

  constructor(
    private httpClient: HttpClient,
    private remoteDiagnosticsSnapshotsService: RemoteDiagnosticsSnapshotsService
  ) {}

  interceptDemo(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<HttpEventType.Response>> {
    if (request.url.match('assets')) {
      return next.handle(request);
    }

    request = modifyRequestUrlForDemoMode(request);
    const url = request.urlWithParams.replace(environment.http.everestBaseUrl, '');
    const isPostRequest = request.method !== 'GET' && this.canMockPostRequest(url);

    return isPostRequest
      ? this.handlePostRequests(request, url)
      : this.handleGetRequest(request, url);
  }

  private handlePostRequests(
    request: HttpRequest<any>,
    url: string
  ): Observable<HttpEvent<HttpEventType.Response>> {
    let body = {};
    let stream$: Observable<any>;

    // *****************************************
    // Demo Endpoints
    // *****************************************

    // Handle POST, PUT, PATCH, DELETE requests WITHOUT specific response
    const stringRequestsRegexpArray: RegExp[] = [
      new RegExp(`dc-diagstack-\\d+\/diagnostic-service\/vci\/connect\/\\S+`),
      new RegExp(`dc-diagstack-\\d+\/diagnostic-service\/logical-link\/open`),
      new RegExp(`dc-diagstack-\\d+\/diagnostic-service\/logical-link\/close\/\\S+`),
      new RegExp(`dc-diagstack-\\d+\/user\/logout`),
      new RegExp(`diagnostic-service\/data\/cancel-cycle\/[a-zA-Z0-9-]+`),
    ];

    // Handle POST, PUT, PATCH, DELETE requests WITH specific TEXT response
    const urlRegexpsWithRequiredTextResponse = [
      new RegExp(`dc-diagstack-\\d+\/diagnostic-generic-otx\/execute`),
      new RegExp(`resources\/technical_documents\/DE\/de\/WCL/.+\\.pdf`),
    ];

    // Handle POST, PUT, PATCH, DELETE requests WITH specific JSON response
    const urlRegexpsWithRequiredJSONResponse = [
      new RegExp(`iot\/v1\/equipment\/installations\/\\d+\/gateways\/.+\/snapshots$`),
    ];

    const remoteServiceResponse = [
      new RegExp(`iot/v\\d+/analytics-api/dataLake/remoteservice/v0/diagnosis`),
      new RegExp(`iot/v\\d+/analytics-api/dataLake/remoteservice/v0/device_features`),
    ];

    const trendingResponse = [
      new RegExp(`iot/v\\d+/analytics-api/dataLake/trending/v0/timeseries`),
    ];

    const hemsResponse = [new RegExp(`iot/v\\d+/analytics-api/dataLake/hems/v0/timeseries`)];

    const thermalEnergyResponse = [
      new RegExp(`iot/v\\d+/analytics-api/dataLake/chronos/v0/thermal_energy`),
    ];

    // *****************************************
    // Matching Responses of POST requests
    // *****************************************

    const isStringRequestsRegexpArray = stringRequestsRegexpArray.some((regexp) =>
      request.url.match(regexp)
    );
    const isUrlRegexpsWithRequiredTextResponse = urlRegexpsWithRequiredTextResponse.some((regexp) =>
      request.url.match(regexp)
    );
    const isUrlRegexpsWithRequiredJSONResponse = urlRegexpsWithRequiredJSONResponse.some((regexp) =>
      request.url.match(regexp)
    );
    const isRemoteServiceResponse = remoteServiceResponse.some((regexp) =>
      request.url.match(regexp)
    );
    const isTrendingResponse = trendingResponse.some((regexp) => request.url.match(regexp));
    const isHemsResponse = hemsResponse.some((regexp) => request.url.match(regexp));
    const isThermalEnergyResponse = thermalEnergyResponse.some((regexp) =>
      request.url.match(regexp)
    );

    if (isStringRequestsRegexpArray) {
      body = '';
    } else if (isUrlRegexpsWithRequiredTextResponse) {
      const requestData = this.mockedData.find((data) => url.match(data.endpoint));
      stream$ = this.httpClient.get(requestData.json, { responseType: 'text' as 'json' });
    } else if (isUrlRegexpsWithRequiredJSONResponse) {
      const requestData = this.mockedData.find((data) => url.match(data.endpoint));
      stream$ = this.httpClient.get(requestData.json);
    } else if (isRemoteServiceResponse) {
      const requestData = this.mockedData.find((data) => {
        const urlMatch = url.match(data.endpoint);
        const details = data?.details as RemoteServiceMockEndpointDetails;
        const boilerSerialMatch = details?.deviceSerial === request.body['boiler_id'];

        return urlMatch && boilerSerialMatch;
      });

      stream$ = this.httpClient.get(requestData.json);
    } else if (isTrendingResponse) {
      const requestData = this.mockedData.find((data) => {
        const urlMatch = url.match(data.endpoint);
        const details = data?.details as TrendingHelperMockEndpointDetails;
        const boilerSerialMatch = details?.gatewaySerial === request.body['gateway_id'];
        const resolutionMatch = details?.resolution === request.body['resolution'];

        return urlMatch && boilerSerialMatch && resolutionMatch;
      });

      stream$ = this.httpClient.get(requestData.json).pipe(
        // Hardcoded data trending charts for 8 days range
        map((data: any) => {
          data.data[0].point_start = moment().subtract(6, 'days').valueOf();

          return data;
        })
      );
    } else if (isHemsResponse) {
      const requestData = this.mockedData.find((data) => {
        const urlMatch = request.url.match(data.endpoint);
        const details = data?.details as TrendingHelperMockEndpointDetails;
        const boilerSerialMatch = details?.gatewaySerial === request.body['gateway_id'];
        const resolutionMatch = details?.resolution === request.body['resolution'];

        return urlMatch && boilerSerialMatch && resolutionMatch;
      });

      stream$ = this.httpClient.get(requestData.json);
    } else if (isThermalEnergyResponse) {
      const requestData = this.mockedData.find((data) => {
        const urlMatch = request.url.match(data.endpoint);
        const details = data?.details as TrendingHelperMockEndpointDetails;
        const boilerSerialMatch = details?.gatewaySerial === request.body['gateway_id'];
        const resolutionMatch = details?.resolution === request.body['resolution'];

        return urlMatch && boilerSerialMatch && resolutionMatch;
      });

      stream$ = this.httpClient.get(requestData.json);
    }

    // *****************************************
    // Logic for matched requests
    // *****************************************

    if (stream$) {
      return stream$.pipe(
        switchMap((res) => {
          return of(
            new HttpResponse<any>({ status: 200, body: res })
          ).pipe(
            // simulate request pending time
            delay(200)
          );
        })
      );
    }

    // *****************************************
    // Logic for everything else
    // *****************************************
    // Pass request without returning data

    return of(
      new HttpResponse<any>({ status: 200, body })
    ).pipe(
      // simulate request pending time
      delay(200)
    );
  }

  private handleGetRequest(
    request: HttpRequest<any>,
    url: string
  ): Observable<HttpEvent<HttpEventType.Response>> {
    const endpointsWithStringToTextResponse: RegExp[] = [
      new RegExp(
        'viessmann-platform.io/dc-diagstack-\\d+/diagnostic-service/data/read-single\\?cycle'
      ),
      new RegExp('/equipment/v\\d+/devices/.+/hydraulic-schema'),
      new RegExp('/equipment/v\\d+/devices/.+/internal-hydraulic-schema'),
    ];
    const endpointsWithDataToJSONResponse: RegExp[] = [
      new RegExp('/viessmann-platform.io/dc-diagstack-\\d+/diagnostic-service/data/read-single/'),
    ];

    for (const data of this.mockedData) {
      if (url.match(data.endpoint)) {
        let stream: Observable<any>;
        // Similar endpoint but with higher priority due to params
        if (
          endpointsWithStringToTextResponse.some((endpoint) =>
            request.urlWithParams.match(endpoint)
          )
        ) {
          // Data in .txt
          stream = this.httpClient.get(data.json, { responseType: 'text' });
        } else if (
          endpointsWithDataToJSONResponse.some((endpoint) => request.url.match(endpoint))
        ) {
          // Problematic endpoint; treat as JSON
          stream = this.httpClient.get(data.json, { responseType: 'json' });
        } else {
          // Each .json file
          stream = this.httpClient.get(data.json);
        }

        return new Observable((o): void => {
          stream
            .pipe(
              // simulate request pending time
              delay(200)
            )
            .subscribe((body) => {
              body = this.modifyPayloadIfNeeded(url, body);
              o.next(
                new HttpResponse<any>({ status: 200, body })
              );
              o.complete();
            });
        });
      }
    }

    return EMPTY;
  }

  private canMockPostRequest(url: string): boolean {
    const forbiddenPostUrls = ['remoteService/recommendations'];

    return !forbiddenPostUrls.some((entry) => url.includes(entry));
  }

  private modifyPayloadIfNeeded(url: string, body: any): any {
    if (
      url.match(
        new RegExp(`iot\/v\\d+\/equipment\/installations\/\.+\/gateways\/\.+\/snapshots\/\.+\/vdds`)
      )
    ) {
      const snapshotId = url.split('/').slice(-2, -1)[0];

      const snapshotVddList = this.remoteDiagnosticsSnapshotsService.snapshotDetailsForDemo.find(
        (snapshot) => snapshot.id === snapshotId
      )?.vddList;

      if (snapshotVddList) {
        return {
          data: snapshotVddList.map((snapshotVdd) => {
            try {
              const vddName = snapshotVdd.key.split('__')[0];
              const mockedVdd: MockedVdd = new Vdds[vddName]();

              return {
                createdAt: '',
                key: vddName,
                logicalLink: snapshotVdd.logicalLink,
                metadata: null,
                structure: mockedVdd.structure(),
                values: mockedVdd.readSingleSync(),
              };
            } catch (err) {
              if (environment.env !== 'production') {
                console.log(
                  `Vdd response for ${snapshotVdd} is missing. Putting ViessmannIdentificationNumber as a replace`
                );
              }
              const vddName = 'ViessmannIdentificationNumber';
              const mockedVdd: MockedVdd = new Vdds[vddName]();

              return {
                createdAt: '',
                key: vddName,
                logicalLink: snapshotVdd.logicalLink,
                metadata: null,
                structure: mockedVdd.structure(),
                values: mockedVdd.readSingleSync(),
              };
            }
          }),
        };
      }

      return body;
    }

    if (
      url.match(
        new RegExp(
          `iot\/v\\d+\/equipment\/installations\/\.+\/gateways\/\.+\/snapshots\\?type=backup`
        )
      )
    ) {
      // Set date as 1st day of current month at 9:00 for backup
      let date = new Date();
      date.setDate(1);
      date.setHours(9);
      date.setMinutes(0);

      return {
        ...body,
        data: body.data.map((entry) => ({ ...entry, createdAt: date.toISOString() })),
      };
    }

    return body;
  }

  createDemoEndpoints(): void {
    this.mockedData = [
      ...initEndpoints(),

      // Vitocal 250-A
      ...installationEndpoints(
        'vitocal-250',
        10000012,
        '0',
        '7720485-device-serial',
        '7736172-gw-serial-12'
      ),
      ...installationEndpoints(
        'vitocal-250',
        10000012,
        'VitosetAqua',
        '7372763-vitoset-aqua-serial',
        '7949719-gw-serial-12'
      ),
      ...installationEndpoints(
        'vitocal-250',
        10000012,
        'ventilation',
        '7372763-ventilation-serial',
        '7736172-gw-serial-12'
      ),

      // Vitocal 252-A
      ...installationEndpoints(
        'vitocal-252-a',
        10000017,
        'RoomControl-1',
        '7720533-device-serial',
        '7736170-gw-serial-17',
        [
          'zigbee-000b57fffeca9dc1',
          'zigbee-000b57fffeca9dc1-1',
          'zigbee-000b57fffeca9dc1-2',
          'zigbee-804b50fffea21166',
          'zigbee-84fd27fffed06d66',
        ]
      ),
      ...installationEndpoints(
        'vitocal-252-a',
        10000017,
        '0',
        '7720533-device-serial',
        '7736170-gw-serial-17'
      ),
      ...installationEndpoints(
        'vitocal-252-a',
        10000017,
        'vitocharge',
        '7372062-vitocharge-serial',
        '7736170-gw-serial-17'
      ),
      ...installationEndpoints(
        'vitocal-252-a',
        10000017,
        'ventilation',
        '7372760-ventilation-serial',
        '7736170-gw-serial-17'
      ),
      ...installationEndpoints(
        'vitocal-252-a',
        10000017,
        'HEMS',
        '7720533-device-serial',
        '7736170-gw-serial-17'
      ),
      ...dataTrendingEndpoints(
        'vitocal-252-a',
        '7736170-gw-serial-17',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitocal-252-a',
        '7736170-gw-serial-17',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitocal-252-a',
        '7736170-gw-serial-17',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitocal-252-a',
        '7736170-gw-serial-17',
        trendingProperties.join(','),
        '1d'
      ),
      ...dataHemsEndpoints('vitocal-252-a', '7736170-gw-serial-17', '15m'),
      ...dataHemsEndpoints('vitocal-252-a', '7736170-gw-serial-17', '1d'),
      ...dataHemsEndpoints('vitocal-252-a', '7736170-gw-serial-17', '1M'),
      ...dataHemsEndpoints('vitocal-252-a', '7736170-gw-serial-17', '1Y'),

      // Vitodens 200-W
      ...installationEndpoints(
        'vitodens-200-w',
        10000018,
        '0',
        '7722311-device-serial',
        '7724820-gw-serial-18'
      ),
      ...activeErrorsEndpoints('vitodens-200-w', '7722311-device-serial', 'F.370', 'DE', 'de'),
      ...activeErrorsEndpoints('vitodens-200-w', '7722311-device-serial', 'F.370', 'GB', 'en'),
      ...activeErrorsEndpoints('vitodens-200-w', '7722311-device-serial', 'F.370'),

      // RoomControl-1
      ...installationEndpoints(
        'RoomControl-1',
        10000000,
        'RoomControl-1',
        '7946868-device-serial',
        '7637415001667188',
        [
          '0',
          'zigbee-000b57fffeca9dc1',
          'zigbee-000b57fffeca9dc1-1',
          'zigbee-000b57fffeca9dc1-2',
          'zigbee-804b50fffea21166',
          'zigbee-84fd27fffed06d66',
        ]
      ),
      ...activeErrorsEndpoints('RoomControl-1', '7946868-device-serial', 'F.473', 'GB', 'en'),
      ...activeErrorsEndpoints('RoomControl-1', '7946868-device-serial', 'F.473', 'DE', 'de'),
      ...activeErrorsEndpoints('RoomControl-1', '7946868-device-serial', 'F.473'),
      ...activeErrorsEndpoints('RoomControl-1', '7946868-device-serial', 'F.13', 'DE', 'de'),
      ...activeErrorsEndpoints('RoomControl-1', '7946868-device-serial', 'F.13', 'GB', 'en'),
      ...activeErrorsEndpoints('RoomControl-1', '7946868-device-serial', 'F.13'),

      // Vitocal 252-A MPK
      ...installationEndpoints(
        'vitocal-252-mpk',
        10000013,
        '0',
        '7202739-device-serial',
        '7637416-gw-serial'
      ),
      ...installationEndpoints(
        'vitocal-252-mpk',
        10000013,
        'HEMS',
        '7202739-device-serial',
        '7637416-gw-serial'
      ),
      ...dataHemsEndpoints('vitocal-252-mpk', '7637416-gw-serial', '15m'),
      ...dataHemsEndpoints('vitocal-252-mpk', '7637416-gw-serial', '1d'),
      ...dataHemsEndpoints('vitocal-252-mpk', '7637416-gw-serial', '1M'),
      ...dataHemsEndpoints('vitocal-252-mpk', '7637416-gw-serial', '1Y'),
      ...dataTrendingEndpoints(
        'vitocal-252-mpk',
        '7637416-gw-serial',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitocal-252-mpk',
        '7637416-gw-serial',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitocal-252-mpk',
        '7637416-gw-serial',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitocal-252-mpk',
        '7637416-gw-serial',
        trendingProperties.join(','),
        '1d'
      ),

      // vitocal-200
      ...installationEndpoints(
        'vitocal-200',
        10000001,
        '0',
        '7733738-device-serial',
        '7637415-gw-serial'
      ),
      ...dataTrendingEndpoints(
        'vitocal-200',
        '7637415-gw-serial',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitocal-200',
        '7637415-gw-serial',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitocal-200',
        '7637415-gw-serial',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitocal-200',
        '7637415-gw-serial',
        trendingProperties.join(','),
        '1d'
      ),

      // vitocal-200-error
      ...installationEndpoints(
        'vitocal-200-error',
        10000009,
        '0',
        '7727496-device-serial',
        '7637419-gw-serial'
      ),
      ...activeErrorsEndpoints('vitocal-200-error', '7727496-device-serial', 'F.10', 'DE', 'de'),
      ...activeErrorsEndpoints('vitocal-200-error', '7727496-device-serial', 'F.10', 'GB', 'en'),
      ...activeErrorsEndpoints('vitocal-200-error', '7727496-device-serial', 'F.10'),
      ...activeErrorsEndpoints('vitocal-200-error', '7727496-device-serial', 'F.544', 'DE', 'de'),
      ...activeErrorsEndpoints('vitocal-200-error', '7727496-device-serial', 'F.544', 'GB', 'en'),
      ...activeErrorsEndpoints('vitocal-200-error', '7727496-device-serial', 'F.544'),
      // vitocal-offline
      ...installationEndpoints(
        'vitocal-offline',
        10000002,
        '0',
        '7733737-device-serial',
        '7637415-gw-serial-7'
      ),

      // vitocharge
      ...installationEndpoints(
        'vitocharge',
        10000010,
        '0',
        '7745667-device-serial',
        '7724827-gw-serial'
      ),
      ...installationEndpoints(
        'vitocharge',
        10000010,
        'HEMS',
        '7745667-device-serial',
        '7724827-gw-serial'
      ),
      ...dataTrendingEndpoints(
        'vitocharge',
        '7724827-gw-serial',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitocharge',
        '7724827-gw-serial',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitocharge',
        '7724827-gw-serial',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitocharge',
        '7724827-gw-serial',
        trendingProperties.join(','),
        '1d'
      ),
      ...dataHemsEndpoints('vitocharge', '7724827-gw-serial', '15m'),
      ...dataHemsEndpoints('vitocharge', '7724827-gw-serial', '1d'),
      ...dataHemsEndpoints('vitocharge', '7724827-gw-serial', '1M'),
      ...dataHemsEndpoints('vitocharge', '7724827-gw-serial', '1Y'),

      // vitodens-100-opentherm
      ...installationEndpoints(
        'vitodens-100-opentherm',
        10000003,
        '0',
        '7570694-device-serial',
        '7637414-gw-serial'
      ),
      ...dataTrendingEndpoints(
        'vitodens-100-opentherm',
        '7637414-gw-serial',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitodens-100-opentherm',
        '7637414-gw-serial',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitodens-100-opentherm',
        '7637414-gw-serial',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitodens-100-opentherm',
        '7637414-gw-serial',
        trendingProperties.join(','),
        '1d'
      ),

      // vitodens-200-maintenance
      ...installationEndpoints(
        'vitodens-200-maintenance',
        10000007,
        '0',
        '7570781-device-serial',
        '7637415-gw-serial-8'
      ),

      // vitodens-300
      ...installationEndpoints(
        'vitodens-300',
        10000008,
        '0',
        '7690296-device-serial',
        '7571381-gw-serial'
      ),
      ...dataTrendingEndpoints(
        'vitodens-300',
        '7571381-gw-serial',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitodens-300',
        '7571381-gw-serial',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitodens-300',
        '7571381-gw-serial',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitodens-300',
        '7571381-gw-serial',
        trendingProperties.join(','),
        '1d'
      ),

      // vitodens-343
      ...installationEndpoints(
        'vitodens-343',
        10000004,
        '0',
        '7570953-device-serial',
        '7571381-gw-serial-4'
      ),

      // vitovalor
      ...installationEndpoints(
        'vitovalor',
        10000005,
        '0',
        '7725048-device-serial',
        '7782093-gw-serial'
      ),
      ...dataTrendingEndpoints(
        'vitovalor',
        '7782093-gw-serial',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitovalor',
        '7782093-gw-serial',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitovalor',
        '7782093-gw-serial',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitovalor',
        '7782093-gw-serial',
        trendingProperties.join(','),
        '1d'
      ),

      // vitovalor-error
      ...installationEndpoints(
        'vitovalor-error',
        10000006,
        '0',
        '7725237-device-serial',
        '7782093-gw-serial'
      ),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.160', 'GB', 'en'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.160', 'DE', 'de'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.160'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.341', 'GB', 'en'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.341', 'DE', 'de'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.341'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.415', 'GB', 'en'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.415', 'DE', 'de'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.415'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.472', 'DE', 'de'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.472', 'GB', 'en'),
      ...activeErrorsEndpoints('vitovalor-error', '7725237-device-serial', 'F.472'),

      // Vitoligno-300
      ...installationEndpoints(
        'vitoligno-300',
        10000011,
        '0',
        '7202733-device-serial',
        '7637221-gw-serial-11'
      ),

      // Vitoair FS 300 E
      ...installationEndpoints(
        'vitoair-fs',
        10000014,
        '0',
        '7892763-device-serial',
        '7949720-gw-serial-14'
      ),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.520', 'DE', 'de'),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.520', 'GB', 'en'),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.520'),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.898', 'DE', 'de'),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.898', 'GB', 'en'),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.898'),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.974', 'DE', 'de'),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.974', 'GB', 'en'),
      ...activeErrorsEndpoints('vitoair-fs', '7892763-device-serial', 'F.974'),
      ...dataTrendingEndpoints(
        'vitoair-fs',
        '7949720-gw-serial-14',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitoair-fs',
        '7949720-gw-serial-14',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitoair-fs',
        '7949720-gw-serial-14',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitoair-fs',
        '7949720-gw-serial-14',
        trendingProperties.join(','),
        '1d'
      ),

      // vitocharge VX3
      ...installationEndpoints(
        'vitocharge-vx3',
        10000015,
        '0',
        '7727518-device-serial',
        '77820930-gw-serial-10'
      ),

      // Vitocal 222-S
      ...installationEndpoints(
        'vitocal-222-s',
        10000016,
        '0',
        '7733747-device-serial',
        '7637410-gw-serial-16'
      ),
      ...dataTrendingEndpoints(
        'vitocal-222-s',
        '7637410-gw-serial-16',
        trendingProperties.join(','),
        '1m'
      ),
      ...dataTrendingEndpoints(
        'vitocal-222-s',
        '7637410-gw-serial-16',
        trendingProperties.join(','),
        '5m'
      ),
      ...dataTrendingEndpoints(
        'vitocal-222-s',
        '7637410-gw-serial-16',
        trendingProperties.join(','),
        '1h'
      ),
      ...dataTrendingEndpoints(
        'vitocal-222-s',
        '7637410-gw-serial-16',
        trendingProperties.join(','),
        '1d'
      ),

      // Remote Diagnostics - Use Cases
      ...useCasesEndpoints('General'),
      ...useCasesEndpoints('System'),
      ...useCasesEndpoints('Network'),
      ...useCasesEndpoints('Hydraulic'),
      // Hydraulic Schemes
      ...hydraulicSchemesEndpoints(),
      // gts
      ...gtsEndpoints('FailureCommunicationWiredRemoteControl'),
      ...gtsEndpoints('InterruptionDomesticHotWaterSensor'),
      // error-database
      ...errorDatabaseErrors(),
      ...backupEndpoints(),
      ...snapshotEndpoints(),
      ...this.mockProFunctionalities(),
    ];
  }

  private mockProFunctionalities(): MockedEndpoint[] {
    const boilerSerialOfInstallationWithProPackage = [
      {
        boilerSerial: '7690296-device-serial',
        deviceName: 'vitodens-300',
      },
      {
        boilerSerial: '7720533-device-serial',
        deviceName: 'vitocal-252-a',
      },
      {
        boilerSerial: '7725048-device-serial',
        deviceName: 'vitovalor',
      },
      {
        boilerSerial: '7733738-device-serial',
        deviceName: 'vitocal-200',
      },
      {
        boilerSerial: '7892763-device-serial',
        deviceName: 'vitoair-fs',
      },
      {
        boilerSerial: '7570694-device-serial',
        deviceName: 'vitodens-100-opentherm',
      },
      {
        boilerSerial: '7745667-device-serial',
        deviceName: 'vitocharge',
      },
      {
        boilerSerial: '7733747-device-serial',
        deviceName: 'vitocal-222-s',
      },
    ];

    return flatten(
      boilerSerialOfInstallationWithProPackage.map((entry) =>
        mockProFunctionalitiesEndpoints(entry.boilerSerial, entry.deviceName)
      )
    );
  }
}
